import { useState } from "react";
import { ChevronDown, ExternalLink, TrendingDown, TrendingUp } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { formatSek } from "@/lib/ai-costs";
import { breakEvenUsage, marginFor, planCost, type PlanCost } from "@/lib/plan-costs";
import type { PlanId } from "@/lib/plans";
import { cn } from "@/lib/utils";

function num(value: number) {
  return new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 2 }).format(value);
}

export function PlanCostRows({ cost }: { cost: PlanCost }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-left text-xs">
        <thead className="text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-3 font-semibold">Funktion</th>
            <th className="py-2 pr-3 text-right font-semibold">Antal/mån</th>
            <th className="py-2 pr-3 text-right font-semibold">Styckpris</th>
            <th className="py-2 pr-3 text-right font-semibold">Summa</th>
            <th className="py-2 font-semibold">Gräns som styr</th>
          </tr>
        </thead>
        <tbody>
          {cost.rows.flatMap((row) => [
            <tr key={row.id} className="border-b border-border/60 align-top">
              <td className="py-2 pr-3">
                <span className="font-medium text-foreground">{row.label}</span>
                <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
                  {row.description}
                </p>
              </td>
              <td className="py-2 pr-3 text-right tabular-nums">{num(row.perMonth)}</td>
              <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">
                {formatSek(row.unitSek, row.unitSek < 1 ? 3 : 2)}
              </td>
              <td className="py-2 pr-3 text-right font-semibold tabular-nums">
                {formatSek(row.totalSek)}
              </td>
              <td className="py-2 text-[11px] text-muted-foreground">{row.limitSource}</td>
            </tr>,
            ...(row.children ?? []).map((child) => (
              <tr key={`${row.id}-${child.label}`} className="border-b border-border/40">
                <td className="py-1.5 pl-4 pr-3 text-[11px] text-muted-foreground">
                  ↳ {child.label}
                </td>
                <td className="py-1.5 pr-3 text-right text-[11px] text-muted-foreground">
                  {child.note}
                </td>
                <td className="py-1.5 pr-3 text-right text-[11px] tabular-nums text-muted-foreground">
                  {formatSek(child.unitSek, 3)}
                </td>
                <td className="py-1.5 pr-3 text-right text-[11px] tabular-nums text-muted-foreground">
                  {formatSek(child.unitSek * row.perMonth)}
                </td>
                <td />
              </tr>
            )),
          ])}
        </tbody>
        <tfoot>
          <tr>
            <td className="py-2 pr-3 font-semibold text-foreground">Tak totalt</td>
            <td />
            <td />
            <td className="py-2 pr-3 text-right font-bold tabular-nums text-foreground">
              {formatSek(cost.maxSek)}
            </td>
            <td className="py-2 text-[11px] text-muted-foreground">allt utnyttjat</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/**
 * Adminruta under ett plankort: sammanfattning av vad planen kan kosta oss,
 * med en utfällbar radtabell. Visas aldrig för vanliga användare.
 */
export function PlanCostBreakdown({
  planId,
  usage = 0.2,
  withLink = true,
  defaultOpen = false,
}: {
  planId: PlanId;
  usage?: number;
  withLink?: boolean;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const cost = planCost(planId);
  const full = marginFor(cost, 1);
  const real = marginFor(cost, usage);
  const be = breakEvenUsage(cost);
  const negative = full.marginSek < 0;

  return (
    <div className="mt-6 rounded-md border border-dashed border-primary/50 bg-ocean-100/40 p-3 text-left">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start justify-between gap-3 text-left"
      >
        <span>
          <span className="block text-[11px] font-semibold uppercase tracking-wide text-primary">
            Adminvy · kostnad för oss
          </span>
          <span className="mt-1 block text-xs text-muted-foreground">
            Tak <strong className="text-foreground">{formatSek(cost.maxSek)}/mån</strong> · vid{" "}
            {Math.round(usage * 100)} % användning{" "}
            <strong className="text-foreground">{formatSek(real.spend)}/mån</strong>
          </span>
          <span
            className={cn(
              "mt-1 flex items-center gap-1 text-xs font-semibold",
              negative ? "text-destructive" : "text-green-500",
            )}
          >
            {negative ? (
              <TrendingDown className="h-3.5 w-3.5" />
            ) : (
              <TrendingUp className="h-3.5 w-3.5" />
            )}
            Marginal vid tak: {formatSek(full.marginSek)}
            {full.marginPct !== null ? ` (${Math.round(full.marginPct)} %)` : ""}
          </span>
        </span>
        <ChevronDown
          className={cn("mt-1 h-4 w-4 shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <PlanCostRows cost={cost} />
          <div className="grid gap-2 text-[11px] text-muted-foreground sm:grid-cols-2">
            <p>
              Intäkt: {formatSek(cost.revenueMonthSek)}/mån (månadsvis) ·{" "}
              {formatSek(cost.revenueYearSek)}/mån (årsvis)
            </p>
            <p>
              {be === null
                ? "Gratisplanen har ingen intäkt – hela kostnaden är förvärvskostnad."
                : `Går back när användaren utnyttjar mer än ${Math.round(be * 100)} % av sina gränser.`}
            </p>
          </div>
          {withLink && (
            <Link
              to="/admin/plankostnader"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
            >
              Se full kalkyl och antaganden <ExternalLink className="h-3 w-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
